import React, { useState, useEffect } from 'react';
import {
  FileText, Plus, Eye, Check, X, Search, Download, Filter,
  Calendar, DollarSign, Hash, AlertCircle, ShoppingCart, Trash2, PlusCircle
} from 'lucide-react';
import { API_URL, getServerUrl } from '../../services/api';
import { generateFactureProPDF } from '../../utils/pdfGenerator';
import { AnomalieBadge, AnomalieDot, chargerAnomalies, estCentral } from '../Common/AnomalieBadge';

const Factures = () => {
  // --- State ---
  const [factures, setFactures] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statutFilter, setStatutFilter] = useState('tous');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  // Modals
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFromVenteModal, setShowFromVenteModal] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState(null);
  // Encaissement facture de service (caisse P4 — D6)
  const [encaissement, setEncaissement] = useState(null); // {facture, montant, mode, boutique_id, reference}
  const [encaissementLoading, setEncaissementLoading] = useState(false);
  // Alertes anomalies A4/A5 (lot 1 — central only, §205)
  const [anomalies, setAnomalies] = useState({});
  const loadAnomalies = async () => { if (estCentral()) setAnomalies(await chargerAnomalies(['A4', 'A5'])); };
  // facture_id -> [{type, item}] pour la pastille de ligne
  const anomParFacture = {};
  ['A4', 'A5'].forEach(t => (anomalies[t]?.items || []).forEach(i => {
    (anomParFacture[i.entite_id] = anomParFacture[i.entite_id] || []).push({ type: t, item: i });
  }));

  // Create form
  const [createForm, setCreateForm] = useState({
    client_nom: '',
    client_telephone: '',
    client_adresse: '',
    client_ice: '',
    boutique_id: '',
    type_facture: 'produit',   // produit = sans TVA ; service = TVA en dedans
    taux_tva: 20,
    notes: '',
    lignes: [{ description: '', reference: '', quantite: 1, prix_unitaire: 0 }]
  });

  // From vente
  const [venteSearch, setVenteSearch] = useState('');
  const [ventesResults, setVentesResults] = useState([]);
  const [ventesLoading, setVentesLoading] = useState(false);

  // Entreprise info for invoice header
  const [entreprise, setEntreprise] = useState({});

  // --- Load data ---
  useEffect(() => {
    loadFactures();
    loadBoutiques();
    loadAnomalies();
    fetch(`${API_URL}/parametres/bijouterie`)
      .then(r => r.json()).then(d => setEntreprise(d || {})).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFactures = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/factures`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setFactures(Array.isArray(data) ? data : (data.factures || []));
    } catch (error) {
      console.error('Erreur chargement factures:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBoutiques = async () => {
    try {
      const response = await fetch(`${API_URL}/boutiques`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setBoutiques(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erreur chargement boutiques:', error);
    }
  };

  // --- Filtering ---
  const filteredFactures = factures.filter(f => {
    if (statutFilter !== 'tous' && f.statut !== statutFilter) return false;
    if (dateDebut && f.date_facture < dateDebut) return false;
    if (dateFin && f.date_facture > dateFin) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (
        (f.numero_facture || '').toLowerCase().includes(s) ||
        (f.client_nom || '').toLowerCase().includes(s) ||
        (f.mode_paiement || '').toLowerCase().includes(s)
      );
    }
    return true;
  });

  // --- Stats ---
  const now = new Date();
  const currentMonth = now.toISOString().slice(0, 7);
  // Stats basees sur le FILTRE actif (meme jeu que la liste) — plus de valeurs figees
  const totalFiltre = filteredFactures.reduce((sum, f) => sum + (parseFloat(f.montant_ttc) || 0), 0);
  const nbBrouillons = filteredFactures.filter(f => f.statut === 'brouillon').length;
  const nbPayees = filteredFactures.filter(f => f.statut === 'payee').length;

  // --- Actions ---
  const voirFacture = async (id) => {
    try {
      const response = await fetch(`${API_URL}/factures/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setSelectedFacture(data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement de la facture');
    }
  };

  const validerFacture = async (id) => {
    if (!window.confirm('Valider cette facture ? Cette action est irréversible.')) return;
    try {
      const response = await fetch(`${API_URL}/factures/${id}/valider`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        loadFactures();
        if (selectedFacture && selectedFacture.id === id) {
          voirFacture(id);
        }
      } else {
        const err = await response.json();
        alert(err.error || 'Erreur lors de la validation');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la validation');
    }
  };

  // Encaissement facture de SERVICE (caisse P4 — D6). Les factures liées à une
  // vente sont encaissées via la vente (anti-double-comptage, décision c).
  const validerEncaissement = async () => {
    if (!encaissement) return;
    const montant = parseFloat(encaissement.montant);
    if (!montant || montant <= 0) { alert('Montant invalide'); return; }
    if (!encaissement.boutique_id) { alert('Choisissez la boutique (caisse)'); return; }
    setEncaissementLoading(true);
    try {
      const response = await fetch(`${API_URL}/factures/${encaissement.facture.id}/encaisser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          montant,
          mode_paiement: encaissement.mode,
          boutique_id: parseInt(encaissement.boutique_id),
          reference: encaissement.reference || null,
        }),
      });
      const d = await response.json();
      if (!response.ok) { alert(d.error || 'Erreur encaissement'); return; }
      if (d.warning === 'caisse_fermee') {
        alert('⚠ Encaissé CAISSE FERMÉE — pensez à ouvrir la caisse (fond du matin)');
      }
      setEncaissement(null);
      loadFactures();
    } catch (e) { alert('Erreur réseau'); }
    finally { setEncaissementLoading(false); }
  };

  const annulerFacture = async (id) => {
    if (!window.confirm('Annuler cette facture ?')) return;
    try {
      const response = await fetch(`${API_URL}/factures/${id}/annuler`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        loadFactures();
        if (selectedFacture && selectedFacture.id === id) {
          voirFacture(id);
        }
      } else {
        const err = await response.json();
        alert(err.error || 'Erreur lors de l\'annulation');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'annulation');
    }
  };

  // --- Create from vente ---
  const searchVentes = async () => {
    if (!venteSearch.trim()) return;
    try {
      setVentesLoading(true);
      const response = await fetch(`${API_URL}/ventes?search=${encodeURIComponent(venteSearch)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setVentesResults(Array.isArray(data) ? data : (data.ventes || []));
    } catch (error) {
      console.error('Erreur recherche ventes:', error);
    } finally {
      setVentesLoading(false);
    }
  };

  const creerDepuisVente = async (venteId) => {
    try {
      const response = await fetch(`${API_URL}/factures/from-vente/${venteId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Facture ${data.numero_facture || ''} créée avec succès !`);
        setShowFromVenteModal(false);
        setVenteSearch('');
        setVentesResults([]);
        loadFactures();
      } else {
        const err = await response.json();
        alert(err.error || 'Erreur lors de la création de la facture');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création');
    }
  };

  // --- Manual create ---
  const resetCreateForm = () => {
    setCreateForm({
      client_nom: '',
      client_telephone: '',
      client_adresse: '',
      client_ice: '',
      boutique_id: '',
      type_facture: 'produit',
      taux_tva: 20,
      notes: '',
      lignes: [{ description: '', reference: '', quantite: 1, prix_unitaire: 0 }]
    });
  };

  const addLigne = () => {
    setCreateForm(prev => ({
      ...prev,
      lignes: [...prev.lignes, { description: '', reference: '', quantite: 1, prix_unitaire: 0 }]
    }));
  };

  const removeLigne = (index) => {
    if (createForm.lignes.length <= 1) return;
    setCreateForm(prev => ({
      ...prev,
      lignes: prev.lignes.filter((_, i) => i !== index)
    }));
  };

  const updateLigne = (index, field, value) => {
    setCreateForm(prev => ({
      ...prev,
      lignes: prev.lignes.map((l, i) => i === index ? { ...l, [field]: value } : l)
    }));
  };

  const calcTotalHT = (lignes) => {
    return lignes.reduce((sum, l) => sum + ((parseFloat(l.quantite) || 0) * (parseFloat(l.prix_unitaire) || 0)), 0);
  };

  const calcTVA = (lignes, taux) => {
    return calcTotalHT(lignes) * (parseFloat(taux) || 0) / 100;
  };

  const calcTotalTTC = (lignes, taux) => {
    return calcTotalHT(lignes) + calcTVA(lignes, taux);
  };

  const submitCreateForm = async () => {
    if (!createForm.client_nom.trim()) {
      alert('Le nom du client est obligatoire');
      return;
    }
    if (createForm.lignes.every(l => !l.description.trim())) {
      alert('Ajoutez au moins une ligne avec une description');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/factures`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...createForm,
          // montant saisi = somme des lignes. produit -> ht=ttc, tva=0 ;
          // service -> TTC encaisse, TVA extraite en dedans cote backend.
          montant_ttc: calcTotalHT(createForm.lignes),
          taux_tva: createForm.type_facture === 'service' ? createForm.taux_tva : undefined,
        })
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Facture ${data.numero_facture || ''} créée avec succès !`);
        setShowCreateModal(false);
        resetCreateForm();
        loadFactures();
      } else {
        const err = await response.json();
        alert(err.error || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la création');
    }
  };

  // --- PDF Generation ---
  // PDF pro unifié (pdfGenerator) — remplace l'ancien template violet inline.
  // cadeau=true : aucun prix nulle part (offrir).
  const generateFacturePDF = (facture, cadeau = false) =>
    generateFactureProPDF(facture, entreprise && entreprise.nom_entreprise ? entreprise : null, { cadeau });

  // --- Helpers ---
  const formatMontant = (val) => {
    return parseFloat(val || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'brouillon': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'emise': 'bg-blue-100 text-blue-800 border-blue-300',
      'payee': 'bg-green-100 text-green-800 border-green-300',
      'annulee': 'bg-red-100 text-red-800 border-red-300'
    };
    return badges[statut] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'brouillon': 'Brouillon',
      'emise': 'Émise',
      'payee': 'Payée',
      'annulee': 'Annulée'
    };
    return labels[statut] || statut;
  };

  // --- Loading ---
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // --- Render ---
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-indigo-600 rounded-2xl shadow-lg p-6 mb-6 text-white">
          <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
            <div>
              <h1 className="text-3xl font-bold flex items-center space-x-3">
                <FileText className="h-8 w-8" />
                <span>Facturation</span>
              </h1>
              <p className="mt-1 text-purple-200">Gérez vos factures clients</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => {
                  setVenteSearch('');
                  setVentesResults([]);
                  setShowFromVenteModal(true);
                }}
                className="w-full sm:w-auto bg-white/20 hover:bg-white/30 text-white px-4 py-2.5 rounded-lg transition-all flex items-center justify-center space-x-2 backdrop-blur-sm"
              >
                <ShoppingCart className="h-5 w-5" />
                <span>Créer depuis une vente</span>
              </button>
              <button
                onClick={() => {
                  resetCreateForm();
                  setShowCreateModal(true);
                }}
                className="w-full sm:w-auto bg-white text-purple-700 px-5 py-2.5 rounded-lg hover:bg-purple-50 transition-all flex items-center justify-center space-x-2 font-semibold shadow-md"
              >
                <Plus className="h-5 w-5" />
                <span>Nouvelle Facture</span>
              </button>
            </div>
          </div>
        </div>

        {/* Alertes anomalies (lot 1 — central only) */}
        {estCentral() && (anomalies.A4 || anomalies.A5) && (
          <div className="flex gap-2 flex-wrap mb-4">
            {anomalies.A4 && <AnomalieBadge type="A4" titre={anomalies.A4.titre} explication={anomalies.A4.explication} items={anomalies.A4.items} onChange={loadAnomalies} />}
            {anomalies.A5 && <AnomalieBadge type="A5" titre={anomalies.A5.titre} explication={anomalies.A5.explication} items={anomalies.A5.items} onChange={loadAnomalies} />}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total TTC (filtré)</p>
                <p className="text-2xl font-bold text-purple-700">{formatMontant(totalFiltre)} MAD</p>
              </div>
              <DollarSign className="h-10 w-10 text-purple-300" />
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Nb factures (filtré)</p>
                <p className="text-2xl font-bold text-indigo-700">{filteredFactures.length}</p>
              </div>
              <Hash className="h-10 w-10 text-indigo-300" />
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Brouillons</p>
                <p className="text-2xl font-bold text-yellow-700">{nbBrouillons}</p>
              </div>
              <AlertCircle className="h-10 w-10 text-yellow-300" />
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Payées</p>
                <p className="text-2xl font-bold text-green-700">{nbPayees}</p>
              </div>
              <Check className="h-10 w-10 text-green-300" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
            <div className="flex items-center space-x-2 flex-1 min-w-[200px]">
              <Search className="h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par n°, client, mode paiement..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={statutFilter}
                onChange={(e) => setStatutFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="tous">Tous les statuts</option>
                <option value="brouillon">Brouillon</option>
                <option value="emise">Émise</option>
                <option value="payee">Payée</option>
                <option value="annulee">Annulée</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Du"
              />
              <span className="text-gray-400">au</span>
              <input
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Au"
              />
            </div>
            {(searchTerm || statutFilter !== 'tous' || dateDebut || dateFin) && (
              <button
                onClick={() => { setSearchTerm(''); setStatutFilter('tous'); setDateDebut(''); setDateFin(''); }}
                className="text-gray-500 hover:text-red-500 transition-colors"
                title="Réinitialiser les filtres"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-sm text-gray-500 mt-2">
              {filteredFactures.length} résultat{filteredFactures.length > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Table — mobile cards */}
        {filteredFactures.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg text-center py-16">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Aucune facture trouvée</p>
            <p className="text-gray-400 text-sm mt-1">Créez votre première facture pour commencer</p>
          </div>
        ) : (
          <>
            <div className="md:hidden space-y-3 mb-4">
              {filteredFactures.map((facture) => (
                <div key={facture.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-400">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-bold text-purple-700 font-mono text-sm">
                        {facture.numero_facture || `#${facture.id}`}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {facture.client_nom || 'N/A'} · {formatDate(facture.date_facture)}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatutBadge(facture.statut)}`}>
                      {getStatutLabel(facture.statut)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <div className="font-bold text-gray-800 text-sm">
                      {formatMontant(facture.montant_ttc)} MAD
                    </div>
                    <button
                      onClick={() => voirFacture(facture.id)}
                      className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs"
                    >
                      Voir
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Table — desktop */}
            <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-purple-50 to-indigo-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">N° Facture</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-purple-700 uppercase tracking-wider">Montant TTC</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-purple-700 uppercase tracking-wider">Mode paiement</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-purple-700 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-purple-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredFactures.map((facture) => (
                    <tr key={facture.id} className="hover:bg-purple-50/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono font-semibold text-purple-700">{facture.numero_facture || `#${facture.id}`}</span>
                        {(anomParFacture[facture.id] || []).map(({ type, item }) => (
                          <AnomalieDot key={type} type={type} item={item} onChange={loadAnomalies} />
                        ))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(facture.date_facture)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{facture.client_nom || 'N/A'}</div>
                        {facture.client_telephone && (
                          <div className="text-xs text-gray-500">{facture.client_telephone}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="font-semibold text-gray-900">{formatMontant(facture.montant_ttc)} MAD</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {facture.mode_paiement || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatutBadge(facture.statut)}`}>
                          {getStatutLabel(facture.statut)}
                        </span>
                        {/* P4 caisse : reste à encaisser (factures sans vente liée) */}
                        {!facture.vente_id && facture.statut !== 'annulee' && facture.statut !== 'payee'
                          && parseFloat(facture.dette_restante || 0) > 0 && (
                          <div className="text-[10px] text-amber-700 font-semibold mt-1">
                            reste {formatMontant(facture.dette_restante)} MAD
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-1">
                          <button
                            onClick={() => voirFacture(facture.id)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Voir"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {/* P4 caisse — encaisser une facture de service (sans vente liée) */}
                          {!facture.vente_id && facture.statut !== 'annulee' && facture.statut !== 'payee'
                            && parseFloat(facture.dette_restante ?? facture.montant_ttc) > 0 && (
                            <button
                              onClick={() => setEncaissement({
                                facture,
                                montant: String(facture.dette_restante ?? facture.montant_ttc),
                                mode: 'especes',
                                boutique_id: facture.boutique_id || '',
                                reference: '',
                              })}
                              className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Encaisser (entre en caisse)"
                            >
                              <DollarSign className="h-4 w-4" />
                            </button>
                          )}
                          {facture.statut === 'brouillon' && (
                            <>
                              <button
                                onClick={() => validerFacture(facture.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Valider"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => annulerFacture(facture.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Annuler"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => {
                              if (selectedFacture && selectedFacture.id === facture.id) {
                                generateFacturePDF(selectedFacture);
                              } else {
                                // Load full facture then generate PDF
                                fetch(`${API_URL}/factures/${facture.id}`, {
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                })
                                  .then(r => r.json())
                                  .then(data => generateFacturePDF(data))
                                  .catch(() => alert('Erreur lors du chargement de la facture'));
                              }
                            }}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Télécharger PDF"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          </>
        )}
      </div>

      {/* =================== DETAIL MODAL =================== */}
      {/* ── Modal encaissement facture de service (caisse P4 — D6) ── */}
      {encaissement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
             onClick={() => setEncaissement(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-1">💵 Encaisser la facture</h3>
            <p className="text-sm text-gray-500 mb-4">
              {encaissement.facture.numero_facture} — reste dû :{' '}
              <strong>{formatMontant(encaissement.facture.dette_restante ?? encaissement.facture.montant_ttc)} MAD</strong>
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Montant encaissé (MAD)</label>
                <input type="number" min="0" step="0.01" value={encaissement.montant}
                  onChange={e => setEncaissement({ ...encaissement, montant: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-400" autoFocus />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Mode de paiement</label>
                <select value={encaissement.mode}
                  onChange={e => setEncaissement({ ...encaissement, mode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
                  <option value="especes">Espèces</option>
                  <option value="carte">Carte</option>
                  <option value="cheque">Chèque</option>
                  <option value="virement">Virement</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Boutique (caisse)</label>
                <select value={encaissement.boutique_id}
                  onChange={e => setEncaissement({ ...encaissement, boutique_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
                  <option value="">Choisir…</option>
                  {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
                </select>
              </div>
              {(encaissement.mode === 'cheque' || encaissement.mode === 'virement') && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Référence (n° chèque / virement)</label>
                  <input type="text" value={encaissement.reference}
                    onChange={e => setEncaissement({ ...encaissement, reference: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button onClick={() => setEncaissement(null)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold">Annuler</button>
              <button onClick={validerEncaissement} disabled={encaissementLoading}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {encaissementLoading ? 'Encaissement…' : 'Encaisser'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && selectedFacture && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-y-auto">

            {/* Toolbar */}
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b rounded-t-2xl sm:rounded-t-2xl">
              <div className="flex items-center gap-2">
                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatutBadge(selectedFacture.statut)}`}>
                  {getStatutLabel(selectedFacture.statut)}
                </span>
                {selectedFacture.statut === 'brouillon' && (
                  <>
                    <button onClick={() => validerFacture(selectedFacture.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors">
                      <Check className="h-3.5 w-3.5" /> Valider
                    </button>
                    <button onClick={() => annulerFacture(selectedFacture.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition-colors">
                      <X className="h-3.5 w-3.5" /> Annuler
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => generateFacturePDF(selectedFacture, true)}
                  className="flex items-center space-x-2 border-2 border-pink-300 text-pink-700 px-4 py-2.5 rounded-lg hover:bg-pink-50 transition-colors font-semibold"
                  title="PDF sans aucun prix — pour offrir">
                  <span>🎁</span>
                  <span>Facture cadeau</span>
                </button>
                <button onClick={() => generateFacturePDF(selectedFacture)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 transition-colors">
                  <Download className="h-3.5 w-3.5" /> PDF
                </button>
                <button onClick={() => setShowDetailModal(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* ════ FACTURE DOCUMENT ════ */}
            <div className="p-6 sm:p-8" style={{ fontFamily: "'Georgia', serif" }}>

              {/* Header entreprise + numéro */}
              <div className="flex items-start justify-between mb-8 pb-6 border-b-2 border-gray-100">
                {/* Logo + Entreprise */}
                <div className="flex items-start gap-4">
                  {entreprise.logo_url ? (
                    <img src={getServerUrl(entreprise.logo_url)} alt="Logo" className="h-14 w-14 object-contain rounded-lg" />
                  ) : (
                    <div className="h-14 w-14 rounded-xl flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #d4af37, #f5e6a3)' }}>
                      <span className="text-2xl font-bold text-gray-900">
                        {(entreprise.nom_entreprise || 'B').charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <div className="text-xl font-bold tracking-wide" style={{ color: '#1a1a2e', fontFamily: 'Georgia, serif' }}>
                      {entreprise.nom_entreprise || 'Bijouterie'}
                    </div>
                    {entreprise.slogan && <div className="text-xs text-gray-400 italic mt-0.5">{entreprise.slogan}</div>}
                    {entreprise.adresse && <div className="text-xs text-gray-500 mt-1">{entreprise.adresse}</div>}
                    {(entreprise.ville || entreprise.pays) && (
                      <div className="text-xs text-gray-500">
                        {[entreprise.ville, entreprise.code_postal, entreprise.pays].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {entreprise.telephone && <div className="text-xs text-gray-500">Tél : {entreprise.telephone}</div>}
                    {entreprise.email && <div className="text-xs text-gray-500">{entreprise.email}</div>}
                    {/* Identifiants légaux */}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5">
                      {entreprise.numero_ice && <span className="text-xs text-gray-400 font-mono">ICE : {entreprise.numero_ice}</span>}
                      {entreprise.numero_if && <span className="text-xs text-gray-400 font-mono">IF : {entreprise.numero_if}</span>}
                      {entreprise.numero_registre_commerce && <span className="text-xs text-gray-400 font-mono">RC : {entreprise.numero_registre_commerce}</span>}
                      {entreprise.numero_patente && <span className="text-xs text-gray-400 font-mono">Patente : {entreprise.numero_patente}</span>}
                    </div>
                  </div>
                </div>

                {/* Numéro facture + date */}
                <div className="text-right">
                  <div className="text-3xl font-bold tracking-tight" style={{ color: '#d4af37', fontFamily: 'Georgia, serif' }}>FACTURE</div>
                  <div className="text-lg font-bold text-gray-700 mt-1 font-mono">{selectedFacture.numero_facture || `#${selectedFacture.id}`}</div>
                  <div className="text-sm text-gray-500 mt-1">{formatDate(selectedFacture.date_facture)}</div>
                  {selectedFacture.mode_paiement && (
                    <div className="text-xs text-gray-400 mt-0.5 capitalize">{selectedFacture.mode_paiement}</div>
                  )}
                  {selectedFacture.paiements && selectedFacture.paiements.length > 0 && (
                    <div className="mt-2 text-left bg-gray-50 rounded-lg p-2 border border-gray-100">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Paiements</div>
                      {selectedFacture.paiements.map((p, idx) => (
                        <div key={idx} className="flex justify-between text-xs text-gray-600">
                          <span className="capitalize">{p.mode}</span>
                          <span className="font-medium">{formatMontant(p.montant)} DH</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Client */}
              <div className="flex justify-end mb-8">
                <div className="w-64 bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Facturé à</div>
                  <div className="font-bold text-gray-900">{selectedFacture.client_nom || 'Client'}</div>
                  {selectedFacture.client_telephone && (
                    <div className="text-sm text-gray-600 mt-0.5">📞 {selectedFacture.client_telephone}</div>
                  )}
                  {selectedFacture.client_adresse && (
                    <div className="text-sm text-gray-600 mt-0.5">{selectedFacture.client_adresse}</div>
                  )}
                  {selectedFacture.client_ice && (
                    <div className="text-xs text-gray-400 font-mono mt-1">ICE : {selectedFacture.client_ice}</div>
                  )}
                </div>
              </div>

              {/* Lignes */}
              <div className="mb-6">
                <table className="w-full">
                  <thead>
                    <tr style={{ background: '#1a1a2e' }}>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider rounded-tl-lg">Description</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Référence</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Qté</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Prix unit.</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider rounded-tr-lg">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedFacture.lignes || []).map((ligne, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                        <td className="px-4 py-3 text-sm text-gray-800">{ligne.description || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono">{ligne.reference || '—'}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-700">{ligne.quantite || 0}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700">{formatMontant(ligne.prix_unitaire)} DH</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                          {formatMontant((ligne.quantite || 0) * (ligne.prix_unitaire || 0))} DH
                        </td>
                      </tr>
                    ))}
                    {(!selectedFacture.lignes || selectedFacture.lignes.length === 0) && (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-400 text-sm">Aucune ligne</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totaux */}
              <div className="flex justify-end mb-6">
                <div className="w-64 space-y-2">
                  {/* Toujours 3 lignes HT/TVA/TTC — valeurs réelles en base, AUCUN recalcul.
                      Taux affiché seulement si tva>0 (pas de % trompeur sur TVA 0,00). */}
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Montant HT</span>
                    <span className="font-medium">{formatMontant(selectedFacture.montant_ht)} DH</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>TVA{parseFloat(selectedFacture.tva || 0) > 0 ? ` (${selectedFacture.taux_tva ?? (selectedFacture.montant_ht > 0 ? (selectedFacture.tva / selectedFacture.montant_ht * 100).toFixed(2) : '')}%)` : ''}</span>
                    <span className="font-medium">{formatMontant(selectedFacture.tva || 0)} DH</span>
                  </div>
                  <div className="border-t-2 border-gray-200 pt-2 flex justify-between items-center">
                    <span className="font-bold text-gray-900">Total TTC</span>
                    <span className="text-2xl font-bold" style={{ color: '#d4af37', fontFamily: 'Georgia, serif' }}>
                      {formatMontant(selectedFacture.montant_ttc)} DH
                    </span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedFacture.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  <p className="text-sm text-amber-800 italic">📝 {selectedFacture.notes}</p>
                </div>
              )}

              {/* Pied de facture */}
              <div className="border-t border-gray-100 pt-4 text-center">
                <p className="text-xs text-gray-400 italic mb-1">Merci pour votre confiance.</p>
                {(entreprise.numero_ice || entreprise.numero_if || entreprise.numero_registre_commerce) && (
                  <p className="text-xs text-gray-300 font-mono">
                    {[
                      entreprise.numero_ice && `ICE : ${entreprise.numero_ice}`,
                      entreprise.numero_if && `IF : ${entreprise.numero_if}`,
                      entreprise.numero_registre_commerce && `RC : ${entreprise.numero_registre_commerce}`,
                    ].filter(Boolean).join('  ·  ')}
                  </p>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* =================== CREATE FROM VENTE MODAL =================== */}
      {showFromVenteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-700 to-indigo-600 p-6 rounded-t-2xl text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold flex items-center space-x-2">
                    <ShoppingCart className="h-6 w-6" />
                    <span>Créer une facture depuis une vente</span>
                  </h2>
                  <p className="text-purple-200 mt-1">Recherchez une vente pour générer automatiquement la facture</p>
                </div>
                <button onClick={() => setShowFromVenteModal(false)} className="text-white/70 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Search */}
              <div className="flex space-x-3 mb-6">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher par ID ou nom du client..."
                    value={venteSearch}
                    onChange={(e) => setVenteSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchVentes()}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={searchVentes}
                  disabled={ventesLoading}
                  className="bg-purple-600 text-white px-5 py-2.5 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                >
                  <Search className="h-4 w-4" />
                  <span>{ventesLoading ? 'Recherche...' : 'Chercher'}</span>
                </button>
              </div>

              {/* Results */}
              {ventesResults.length > 0 ? (
                <div className="space-y-3">
                  {ventesResults.map((vente) => (
                    <div
                      key={vente.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:bg-purple-50/30 transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="flex items-center space-x-3">
                            <span className="font-mono font-semibold text-purple-700">Vente #{vente.id}</span>
                            <span className="text-sm text-gray-500">{formatDate(vente.date_vente || vente.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">
                            Client: <span className="font-medium">{vente.client_nom || vente.nom_client || 'Anonyme'}</span>
                          </p>
                          <p className="text-sm text-gray-600">
                            Montant: <span className="font-semibold">{formatMontant(vente.montant_total || vente.total)} MAD</span>
                            {vente.boutique_nom && <span className="ml-3 text-gray-400">| {vente.boutique_nom}</span>}
                          </p>
                        </div>
                        <button
                          onClick={() => creerDepuisVente(vente.id)}
                          className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all flex items-center space-x-2 text-sm shadow-md"
                        >
                          <FileText className="h-4 w-4" />
                          <span>Facturer</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : venteSearch && !ventesLoading ? (
                <div className="text-center py-8">
                  <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Aucune vente trouvée pour cette recherche</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Saisissez un terme de recherche pour trouver une vente</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* =================== MANUAL CREATE MODAL =================== */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-700 to-indigo-600 p-6 rounded-t-2xl text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold flex items-center space-x-2">
                    <Plus className="h-6 w-6" />
                    <span>Nouvelle Facture</span>
                  </h2>
                  <p className="text-purple-200 mt-1">Créez une facture manuellement</p>
                </div>
                <button onClick={() => setShowCreateModal(false)} className="text-white/70 hover:text-white">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Client Info */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Informations client</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom du client *</label>
                    <input
                      type="text"
                      value={createForm.client_nom}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, client_nom: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Nom complet"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="text"
                      value={createForm.client_telephone}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, client_telephone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="06XXXXXXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                    <input
                      type="text"
                      value={createForm.client_adresse}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, client_adresse: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Adresse complète"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ICE</label>
                    <input
                      type="text"
                      value={createForm.client_ice}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, client_ice: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Identifiant commun de l'entreprise"
                    />
                  </div>
                </div>
              </div>

              {/* Boutique & TVA */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Paramètres facture</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Boutique</label>
                    <select
                      value={createForm.boutique_id}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, boutique_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">-- Sélectionner --</option>
                      {boutiques.map(b => (
                        <option key={b.id} value={b.id}>{b.nom}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de facture</label>
                    <div className="flex gap-1">
                      <button type="button"
                        onClick={() => setCreateForm(prev => ({ ...prev, type_facture: 'produit' }))}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${createForm.type_facture === 'produit' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                        Produit
                      </button>
                      <button type="button"
                        onClick={() => setCreateForm(prev => ({ ...prev, type_facture: 'service' }))}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${createForm.type_facture === 'service' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                        Service
                      </button>
                    </div>
                    {createForm.type_facture === 'produit' ? (
                      <p className="text-xs text-gray-400 mt-1">Vente de bijou — sans TVA</p>
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <label className="text-xs text-gray-500">Taux TVA (%)</label>
                        <input
                          type="text" inputMode="decimal" onFocus={e => e.target.select()}
                          value={createForm.taux_tva}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, taux_tva: parseFloat(e.target.value) || 0 }))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span className="text-xs text-gray-400">en dedans (prix saisi = TTC)</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      type="text"
                      value={createForm.notes}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Notes ou commentaires"
                    />
                  </div>
                </div>
              </div>

              {/* Lines */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase">Lignes de facture</h3>
                  <button
                    onClick={addLigne}
                    className="text-purple-600 hover:text-purple-800 transition-colors flex items-center space-x-1 text-sm font-medium"
                  >
                    <PlusCircle className="h-4 w-4" />
                    <span>Ajouter une ligne</span>
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Description</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Référence</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 w-20">Qte</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 w-32">Prix unit.</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 w-32">Montant</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {createForm.lignes.map((ligne, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={ligne.description}
                              onChange={(e) => updateLigne(idx, 'description', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                              placeholder="Description de l'article"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={ligne.reference}
                              onChange={(e) => updateLigne(idx, 'reference', e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm font-mono"
                              placeholder="Réf."
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text" inputMode="decimal" onFocus={e => e.target.select()}
                              value={ligne.quantite}
                              onChange={(e) => updateLigne(idx, 'quantite', parseInt(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-center"
                              min="1"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text" inputMode="decimal" onFocus={e => e.target.select()}
                              value={ligne.prix_unitaire}
                              onChange={(e) => updateLigne(idx, 'prix_unitaire', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm text-right"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-3 py-2 text-right text-sm font-semibold text-gray-700">
                            {formatMontant((parseFloat(ligne.quantite) || 0) * (parseFloat(ligne.prix_unitaire) || 0))} MAD
                          </td>
                          <td className="px-3 py-2 text-center">
                            {createForm.lignes.length > 1 && (
                              <button
                                onClick={() => removeLigne(idx)}
                                className="text-red-400 hover:text-red-600 transition-colors"
                                title="Supprimer la ligne"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-end">
                  <div className="w-72 space-y-2">
                    {createForm.type_facture === 'service' ? (
                      (() => {
                        const ttc = calcTotalHT(createForm.lignes);              // somme lignes = TTC encaisse
                        const ht = ttc / (1 + (parseFloat(createForm.taux_tva) || 0) / 100);
                        return (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Montant HT</span>
                              <span className="font-medium">{formatMontant(ht)} MAD</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">TVA ({createForm.taux_tva}% en dedans)</span>
                              <span className="font-medium">{formatMontant(ttc - ht)} MAD</span>
                            </div>
                            <div className="border-t border-gray-300 pt-2 flex justify-between">
                              <span className="font-bold text-gray-800">Total TTC</span>
                              <span className="font-bold text-purple-700 text-lg">{formatMontant(ttc)} MAD</span>
                            </div>
                          </>
                        );
                      })()
                    ) : (
                      <div className="border-t border-gray-300 pt-2 flex justify-between">
                        <span className="font-bold text-gray-800">Total (sans TVA)</span>
                        <span className="font-bold text-purple-700 text-lg">
                          {formatMontant(calcTotalHT(createForm.lignes))} MAD
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={submitCreateForm}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-2.5 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all flex items-center space-x-2 shadow-md font-semibold"
                >
                  <FileText className="h-5 w-5" />
                  <span>Créer la facture</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Factures;
